<template>
  <div v-if="!loading" class="payroll-request-summary" redesign>
    <div class="local-hero local-hero-dark no-padding">
      <div class="local-hero-graphic">
        <div class="local-hero-header-wrapper">
          <h1 class="local-hero-header">{{ header }}</h1>
        </div>
        <div class="local-hero-graphic-wrapper hidden-mobile">
          <div class="local-hero-graphic-holder tree-planting">
            <TreePlantingSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>

    <div class="form-container payroll-request-summary-form">
      <!-- Information -->
      <div class="form-header">
        <span v-translate="{ email: organizationDetails.payrollEmail }">
          Your request has been emailed to your payroll department. Should you wish to change your
          payroll deductions, contact your payroll department directly at %{email}.
        </span>
      </div>

      <!-- Donation details -->
      <div class="donation-summary">
        <!-- Payment amount -->
        <div class="summary-item">
          <div class="field-name" v-translate>Amount</div>
          <div class="field-value">{{ payrollRequest.amount | currency }}</div>
        </div>

        <!-- Payment method -->
        <div class="summary-item">
          <div class="field-name" v-translate>Payment method</div>
          <div class="field-value" v-translate>Payroll Deduction</div>
        </div>

        <!-- Payment frequency -->
        <div class="summary-item">
          <div class="field-name" v-translate>Frequency</div>
          <div class="field-value">{{ $gettext(formattedFrequency) }}</div>
        </div>

        <!-- Payment date -->
        <div class="summary-item">
          <div class="field-name" v-translate>Date</div>
          <div class="field-value">
            {{ this.payrollRequest.datetimeCreated | formatDateShort($language.current) }}
          </div>
        </div>
      </div>

      <!-- FAQ CTA -->
      <div class="form-footer">
        <span class="inherit-font" v-translate>For information on the tax status of donations,</span
        >&nbsp; <router-link :to="'/faq#16'" v-translate>see the FAQ</router-link>.
      </div>

      <!--<LoadingSpinnerButton class="button" :text="this.home()" @click="onClickHome" />-->
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'
import AnonymousDonor from '@/services/anonymousDonors'
import PayrollRequest from '@/services/payrollRequests'
import TreePlantingSvg from '@/views/svg/TreePlantingSvg'

export default {
  name: 'PayrollSummary',
  components: {
    LoadingSpinnerButton,
    TreePlantingSvg,
  },
  data() {
    return {
      loading: true,
      payrollRequest: PayrollRequest.create({}),

      // Anonymous donor info
      anonymousDonorId: null,
      magicToken: null,
      anonymousDonor: AnonymousDonor.create({}),
    }
  },
  async created() {
    // First, handle any anonymous donor auth credentials passed in
    if (this.$route.query.donorId && this.$route.query.magicToken) {
      await this.retrieveAnonymousDonor()
    }
    // Next, populate this screen with a payroll request.
    // If we've received a Payroll Request ID as a route param, use that
    if (this.$route.params.id) {
      await this.populateFromPayrollRequest(this.$route.params.id)
      // Finally, if this page is inappropriately accessed without a payroll request, route home
    } else {
      this.$router.push({ name: 'Home' })
      return
    }

    this.loading = false
  },
  methods: {
    home() {
      return this.$gettext('Home')
    },
    async retrieveAnonymousDonor() {
      this.anonymousDonorId = this.$route.query.donorId
      this.magicToken = this.$route.query.magicToken
      const anonymousDonor = await AnonymousDonor.api.retrieve(
        this.anonymousDonorId,
        this.anonymousDonorParams,
      )
      this.anonymousDonor = anonymousDonor
    },
    /* Populate this page with info from a payroll request in our database. */
    async populateFromPayrollRequest(payrollRequestId) {
      let result
      if (this.userIsLoggedIn) {
        result = await PayrollRequest.api.retrieve(payrollRequestId)
      } else {
        result = await PayrollRequest.api.retrieve(payrollRequestId, this.anonymousDonorParams)
      }
      this.payrollRequest = result
    },
    onClickHome() {
      this.$router.push({ name: 'Home' })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentPayrollRequest']),
    ...mapGetters(['userIsLoggedIn']),
    formattedFrequency() {
      return PayrollRequest.options.FREQUENCY_CHOICES.find(
        choice => choice.value === this.payrollRequest.frequency,
      ).label
    },
    /* Format anonymous donor authentication info to be sent as params to API */
    anonymousDonorParams() {
      return { donorId: this.anonymousDonorId, magicToken: this.magicToken }
    },
    header() {
      const translated = [
        this.$gettext('Thank you for supporting the'),
          this.$options.filters.translateToCurrentLang(this.$language.current, '!', ` ${this.organizationDetails.programName}!`),
      ]
      return `${translated[0]} ${this.organizationDetails.name}${translated[1]}`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.payroll-request-summary {
  display: flex;
  flex-direction: column;

  .payroll-request-summary-form {
    @extend .constrain-tablet-and-up;
  }
}

.tree-planting {
  align-items: end !important;
}

.donation-summary {
  display: flex;

  @include mobile-screen {
    margin-left: 5rem;
    margin-right: 5rem;
    flex-direction: column;
  }

  @include tablet-and-up-screen {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

.summary-item {
  background-color: rgba(0, 42, 139, 0.1);
  border-radius: 12px;
  margin-bottom: 1.5rem;
  padding: 1.5rem;

  @include tablet-and-up-screen {
    width: 48%;
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    &:nth-child(1),
    &:nth-child(3) {
      margin-left: 0;
    }

    &:nth-child(2),
    &:nth-child(4) {
      margin-right: 0;
    }
  }
}
</style>
